import { CSSProperties, FC } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { Icon } from '../atoms/icon';
import { Link } from '../atoms/link';
import { Container } from '../Container';
import { Slider } from '../Slider';
import classNames from 'classnames';
import { LinksCarouselSlice } from '@/types';
import { normalizePath } from '@/utils';
import { PrismicImageWithAlt } from '../PrismicImageWithAlt';
import styles from './linkscarousel.module.scss';

const LinksCarousel: FC<{ slice?: LinksCarouselSlice }> = ({ slice }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const active = slice?.primary?.active;
  const activeAutoplay = slice?.primary?.autoplay;
  const delay = slice?.primary?.autoplay_delay ?? 2500;
  const waitForTransition = !!slice?.primary?.wait_for_transition;
  const disableOnInteraction =
    !!slice?.primary?.disable_autoplay_on_interaction;
  const speed = slice?.primary?.autoplay_speed ?? 1000;
  const background = slice?.primary?.background_color;
  const backgroundItems = slice?.primary?.background_items_color;
  const backgroundItemsHover = slice?.primary?.background_items_hover_color;
  const titleColor = slice?.primary?.title_color;
  const titleColorHover = slice?.primary?.title_color_hover;
  const borderColorItems = slice?.primary?.border_items_color;
  const borderColorItemsHover = slice?.primary?.border_items_hover_color;
  const borderBottom = slice?.primary?.border_bottom;
  const borderBottomWidth = slice?.primary?.border_bottom_width;
  const borderBottomColor = slice?.primary?.border_bottom_color;
  const borderItems = slice?.primary?.border_width_items;
  const borderRadiusItems = slice?.primary?.border_radius_items;
  const marginBottom = slice?.primary?.bottom_spacing;
  const showIcon = slice?.primary?.show_angle_icon;
  const disabledPadding = slice?.primary?.disable_section_padding;
  const borderInsideContainer = slice?.primary?.border_section_inside_container;
  const isItemLinkSimple = !borderItems && !backgroundItems;
  const spacing = isItemLinkSimple ? 16 : 8;
  const dimensions = slice?.primary?.dimension ?? 'small';

  const isBorderBottom = borderBottom && borderBottomWidth && borderBottomColor;
  const isBorderItems = borderItems && borderColorItems;

  const breakpointsDefault = {
    640: {
      spaceBetween: 8,
    },
  };

  const breakpointsSimple = {
    640: {
      spaceBetween: 16,
    },
  };

  const breakpoints = isItemLinkSimple ? breakpointsSimple : breakpointsDefault;

  const sliderKey = `${pathname ?? ''}${
    searchParams?.toString() ? `?${searchParams.toString()}` : ''
  }`;

  const sectionStyle = {
    '--links-carousel-link-border-hover': borderColorItemsHover,
    '--links-carousel-link-background-hover': backgroundItemsHover,
    '--links-carousel-link-title-hover': titleColorHover,
    background,
    borderBottom:
      isBorderBottom && !borderInsideContainer
        ? `${borderBottomWidth}px solid ${borderBottomColor}`
        : undefined,
  } as CSSProperties;

  const containerStyle: CSSProperties = {
    borderBottom:
      isBorderBottom && borderInsideContainer
        ? `${borderBottomWidth}px solid ${borderBottomColor}`
        : undefined,
  };

  const currentPath = normalizePath(pathname ?? '');
  const items = slice?.items ?? [];

  if (!active || !items.length) {
    return null;
  }

  return (
    <section
      className={classNames(styles.links, {
        [styles.links_with_spacing]: !disabledPadding && !borderInsideContainer,
        spacing_bottom: marginBottom,
      })}
      style={sectionStyle}
    >
      <Container
        className={classNames({
          [styles.links_container_with_spacing]:
            !disabledPadding && borderInsideContainer,
          [styles['links_container--full-width']]: slice?.primary?.full_width,
        })}
        style={containerStyle}
      >
        <Slider
          activeAutoplay={activeAutoplay}
          ariaLabel="Links carousel"
          autoplay={{ delay, disableOnInteraction, waitForTransition }}
          breakpoints={breakpoints}
          key={sliderKey}
          loop={activeAutoplay}
          showSliderHeader={false}
          showSlidesOutsideContainer
          spaceBetween={spacing}
          speed={speed}
        >
          {items.map((item, index) => {
            const href = item?.link?.trim();
            const isCurrent = !!href && normalizePath(href) === currentPath;
            const itemClassName = classNames(
              'carousel_links__link',
              styles.carousel_links__link,
              styles[`carousel_links__link--${dimensions}`],
              {
                [styles.link_static]: !href,
                [styles.link_current]: isCurrent,
              },
            );
            const itemStyle = {
              background: backgroundItems!,
              border: isBorderItems
                ? `${borderItems}px solid ${borderColorItems}`
                : undefined,
              borderRadius: borderRadiusItems
                ? `${borderRadiusItems}px`
                : undefined,
              color: titleColor!,
            } as CSSProperties;
            const itemContent = (
              <>
                <figure
                  className={classNames(
                    styles.carousel_links__link__image,
                    styles[`carousel_links__link__image--${dimensions}`],
                  )}
                >
                  <PrismicImageWithAlt
                    defaultAlt={
                      item?.title ? `${item.title} icon` : 'Link icon'
                    }
                    field={item?.icon}
                  />
                </figure>
                {item?.title}
                {showIcon && href && !isCurrent && <Icon name="angle-right" />}
              </>
            );

            if (!href) {
              return (
                <span className={itemClassName} key={index} style={itemStyle}>
                  {itemContent}
                </span>
              );
            }

            return (
              <Link
                aria-current={isCurrent ? 'page' : undefined}
                aria-label={
                  isCurrent
                    ? `${item?.title}, current page`
                    : `Go to ${item?.title}`
                }
                className={itemClassName}
                href={href}
                key={index}
                style={itemStyle}
              >
                {itemContent}
              </Link>
            );
          })}
        </Slider>
      </Container>
    </section>
  );
};

export { LinksCarousel };
